import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  linkedSignal,
  output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IncomeTestAdjustmentRequest } from '@interfaces/partner-income-tax/requests/partner-income-tax.request';
import {
  IncomeTestDto,
  IncomeTestFieldKey,
} from '@interfaces/partner-income-tax/responses/partner-income-tax.response';
import { SharedModule } from '@shared/shared.module';
import {
  INCOME_TEST_FIELDS,
  availableResources,
  buildIncomeTestAdjustment,
  incomeTestResult,
  incomeTestValuesFrom,
  isFieldAdjusted,
  isFieldChanged,
} from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/utils/income-test.util';
import { formatCompactMoney } from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/utils/partner-income-tax-format.util';

/**
 * "Renda que sobra após o crescimento patrimonial": o teste de acréscimo patrimonial a descoberto com os cinco
 * campos editáveis. O resultado muda a cada tecla (prévia local, ver `income-test.util.ts`); a saída do campo
 * grava pelo PUT e "Restaurar valores da declaração" chama o DELETE.
 */
@Component({
  selector: 'app-pit-income-test-panel',
  standalone: true,
  imports: [FormsModule, SharedModule],
  templateUrl: './income-test-panel.component.html',
  styleUrl: './income-test-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IncomeTestPanelComponent {
  readonly test = input.required<IncomeTestDto>();
  readonly canEdit = input(false);
  readonly busy = input(false);

  /** Prévia do resultado, para o indicador do topo acompanhar a digitação. */
  readonly liveResult = output<number>();
  readonly adjust = output<{ returnId: string; request: IncomeTestAdjustmentRequest }>();
  readonly restore = output<string>();

  protected readonly fields = INCOME_TEST_FIELDS;
  protected readonly money = formatCompactMoney;

  /** Valores na tela, em reais. Voltam ao do backend sempre que chega um teste novo. */
  protected readonly values = linkedSignal(() => incomeTestValuesFrom(this.test()));

  protected readonly resources = computed(() => availableResources(this.values()));
  protected readonly result = computed(() =>
    incomeTestResult(this.values(), this.test().netWorthIncrease)
  );
  protected readonly compatible = computed(() => this.result() >= 0);
  protected readonly anyAdjusted = computed(
    () =>
      this.test().adjusted ||
      this.fields.some(field => isFieldAdjusted(this.test(), field.key, this.values()[field.key]))
  );
  protected readonly livingCostMissing = computed(() => this.values().livingCost == null);

  constructor() {
    effect(() => this.liveResult.emit(this.result()));
  }

  /**
   * Valor do campo em R$ mil, como o protótipo mostra, com uma casa decimal (18,7) quando a fração em mil é
   * relevante; o campo esconde o ",0" (`minFractionDigits` 0).
   */
  protected thousands(key: IncomeTestFieldKey): number | null {
    const value = this.values()[key];
    return value == null ? null : Math.round(value / 100) / 10;
  }

  protected isAdjusted(key: IncomeTestFieldKey): boolean {
    return isFieldAdjusted(this.test(), key, this.values()[key]);
  }

  /**
   * O campo numérico devolve o valor em R$ mil. Só vira mudança quando difere do que já está na tela (o
   * `p-inputNumber` emite o próprio valor arredondado ao perder o foco, o que não é edição do analista).
   */
  protected onValue(key: IncomeTestFieldKey, thousands: number | string | null): void {
    const parsed = thousands === null || thousands === '' ? null : Number(thousands);
    const next = parsed == null || !Number.isFinite(parsed) ? null : parsed;
    if (next === this.thousands(key)) return;
    this.values.update(values => ({ ...values, [key]: next == null ? null : next * 1_000 }));
  }

  /** Saída do campo: grava só se o valor mudou em relação ao que está salvo. */
  protected onBlur(key: IncomeTestFieldKey): void {
    if (!this.canEdit()) return;
    const value = this.values()[key];
    if (!isFieldChanged(this.test(), key, value)) return;
    this.adjust.emit({
      returnId: this.test().returnId,
      request: buildIncomeTestAdjustment(key, value),
    });
  }
}
